import * as React from "react";
import { useRef, MutableRefObject, useEffect, useState } from "react";
import { useMapContext } from "../Contexts/MapContext";
import { Map } from "ol";
import { useMap } from "./useMap";
import { HelpPopup } from "../Popups/HelpPopup";
import { Toolbar } from "./Toolbar/Toolbar";
import { BaseMapWidget } from "./Basemaps/BasemapWidget";
import { BaseMapContextProvider } from "../Contexts/BaseMapContext";
import { CoordinateWidget } from "./Coordinates/CoordinateWidget";
import { ScaleWidget } from "./Scale/ScaleWidget";
import { BasemapTools } from "./Basemaps/BasemapTools";
import { ShrinkCoordsButton } from "./Coordinates/ShrinkCoordsButton";
import { Identify } from "./Identify/Identify";
import { SideLogo } from "./Logos/SideLogo";
import { SearchWidget } from "./Search/SearchWidget";
import { SearchContextProvider } from "../Contexts/SearchContext";
import { IdentifyContextProvider } from "../Contexts/IdentifyContext";
import { IdentifyPopup } from "./Identify/IdentifyPopup";
import { Widget } from "./enums";
import { TocWidget } from "./Toc/TocWidget";
import { LayersContextProvider } from "../Contexts/LayersContext";
import { WidgetPopup } from "./Widget/WidgetPopup";
import { PrintWidget } from "./Print/PrintWidget";
import { PrintContextProvider } from "../Contexts/PrintContext";
import { Info } from "../Popups/Info/Info";
import { useAppContext } from "../Contexts/AppContext";
import { DrawingContextProvider } from "../Contexts/DrawingContext";
import { DrawingWidget } from "./Drawing/DrawingWidget";
import { BookmarksWidget } from "./Bookmarks/BookmarksWidget";
import { ConvertWidget } from "./Convert/ConvertWidget";
import { ConvertContextProvider } from "../Contexts/ConvertContext";
import { Tooltip } from "./Tooltip/Tooltip";
import { UpperLogo } from "./Logos/UpperLogo";
import { HomeButton } from "./Buttons/Home";
import { GeoLocationButton } from "./Buttons/GeoLocation";
import { MapDownloadWidget } from "./MapDownload/MapDownloadWidget";
import { getUrlParam } from "./utils";
import { NewsPopup } from "./News/NewsPopup";
import { ElevationButton } from "./Buttons/ElevationButton";
import { ElevationPopup } from "./Elevation/ElevationPopup";
import "./GeoLiteMap.scss";
import "ol-ext/dist/ol-ext.css";

let mapRef: MutableRefObject<Map>;

export const GeoLiteMap = (): JSX.Element => {
    const [forceUpdate, setForceUpdate] = useState<number>(0);

    const { info, setLanguage, isMobile } = useAppContext();
    const { widget, drawingInfoVisible, configA, baseMap, coordsExpanded } = useMapContext();

    const ref = useRef<HTMLDivElement>();
    mapRef = useRef<Map>();
    mapRef.current = useMap(ref, { features: null, forceUpdate });

    useEffect(() => {
        if (!configA) {
            return;
        }
        console.log("config", configA);
        document.title = configA.head.title;

        const defaultLanguage =
            /* localStorage.getItem("GeoLiteLanguage") ||*/ getUrlParam("lang") || configA?.language || "LV";
        setLanguage(defaultLanguage.toUpperCase());
    }, [configA]);

    const widgets = configA && configA.widgets;
    const isMultipleBasemaps = configA && configA.basemaps && configA.basemaps.length > 1;
    const isBasemapWidgetEnabled = widgets && (widgets.Basemaps || widgets.Basemaps === undefined);

    const hasLayersWidget = widgets && (widgets.LayerList || widgets.LayerList === undefined);
    const hasSearchWidget = widgets && (widgets.Search || widgets.Search === undefined);
    const hasDrawWidget = widgets && (widgets.Sketch || widgets.Sketch === undefined);
    const hasBookmarksWidget = widgets && (widgets.Bookmarks || widgets.Bookmarks === undefined);
    const hasConversionWidget = widgets && (widgets.CoordinateConversion || widgets.CoordinateConversion === undefined);

    return (
        <>
            <div className="map-container">
                <div ref={ref} className="map" />
                <div id="print-frame-container" />
            </div>

            {configA && mapRef.current ? (
                <IdentifyContextProvider>
                    <Identify />
                    <IdentifyPopup />
                </IdentifyContextProvider>
            ) : null}

            <Toolbar />

            {configA && baseMap && mapRef.current && isBasemapWidgetEnabled && isMultipleBasemaps ? (
                <BaseMapContextProvider>
                    <BaseMapWidget map={mapRef.current} />
                    <BasemapTools map={mapRef.current} />
                </BaseMapContextProvider>
            ) : null}

            {mapRef.current ? (
                <LayersContextProvider>
                    <SearchContextProvider>
                        <ConvertContextProvider>
                            <DrawingContextProvider>
                                {hasLayersWidget ||
                                hasSearchWidget ||
                                hasDrawWidget ||
                                hasBookmarksWidget ||
                                hasConversionWidget ? (
                                    <WidgetPopup>
                                        {widget === Widget.Toc ? (
                                            <TocWidget mainToc={true} />
                                        ) : widget === Widget.Search ? (
                                            <SearchWidget />
                                        ) : widget === Widget.Print ? (
                                            <PrintContextProvider>
                                                <PrintWidget />
                                            </PrintContextProvider>
                                        ) : widget === Widget.Convert ? (
                                            <ConvertWidget />
                                        ) : widget === Widget.Draw ? (
                                            <DrawingWidget />
                                        ) : widget === Widget.Bookmarks ? (
                                            <DrawingContextProvider>
                                                <BookmarksWidget />
                                            </DrawingContextProvider>
                                        ) : widget === Widget.MapDownload ? (
                                            <MapDownloadWidget />
                                        ) : null}
                                    </WidgetPopup>
                                ) : null}
                            </DrawingContextProvider>
                        </ConvertContextProvider>
                    </SearchContextProvider>
                </LayersContextProvider>
            ) : null}

            {configA && mapRef.current ? <Tooltip /> : null}

            <div className="top-widget-line ol-unselectable">
                <div className="left">
                    {mapRef.current ? (
                        <>
                            <UpperLogo props={configA.customerRow} />
                            <HomeButton map={mapRef.current} />
                            <GeoLocationButton map={mapRef.current} />
                        </>
                    ) : null}
                    <div id="zoom-button-container" />
                    {!isMobile && <ElevationButton />}
                </div>
            </div>

            <div className="bottom-widget-line ol-unselectable">
                <div className="right">
                    {mapRef.current ? (
                        <>
                            <SideLogo props={configA.customerRow} />

                            {coordsExpanded ? (
                                <>
                                    <ScaleWidget map={mapRef.current} />
                                    <CoordinateWidget map={mapRef.current} />{" "}
                                </>
                            ) : null}
                            <ShrinkCoordsButton />
                        </>
                    ) : null}
                </div>
            </div>

            {drawingInfoVisible ? <HelpPopup></HelpPopup> : null}
            {info ? <Info></Info> : null}
            {configA?.popup ? <NewsPopup /> : null}
            {!isMobile && mapRef.current ? <ElevationPopup /> : null}
        </>
    );
};

export const getMap = (): Map => mapRef.current;
